import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, ChevronRight } from 'lucide-react';
import LikeButton from './LikeButton';
import { useAuth } from '../context/AuthContext';
import { useTags } from '../context/TagsContext';
import { toTitleCase } from '../utils/format';

const ImageCard = ({
  image,
  showPhotographer = true,
  showTags = false,
  photographerClickable = false,
  tagsClickable = false,
  showLike = true,
  cropToSquare = false,
  priority = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const { resolveTagSlug } = useTags();

  const photoUrl = `/photos/${image.id}-${image.alias}`;

  const photographer = image.photographer;
  const { user: currentUser } = useAuth();
  // Hide the heart on a user's own photos — they can't self-like.
  const isOwnPhoto = !!(currentUser && image.user_id && currentUser.user_id === image.user_id);
  const showLikeFinal = showLike && !isOwnPhoto;
  const photographerName = toTitleCase(photographer?.user_display_name || 'Unknown');
  const photographerImage = photographer?.user_image_url;
  const photographerUrl = photographer?.user_url;

  const handlePhotographerClick = (e) => {
    if (photographerClickable && photographerUrl) {
      e.preventDefault();
      e.stopPropagation();
      navigate(photographerUrl);
    }
  };

  const handleTagClick = (e, tag) => {
    if (tagsClickable) {
      e.preventDefault();
      e.stopPropagation();
      const slug = resolveTagSlug(tag);
      if (slug) navigate(`/photos-hd/${slug}`);
    }
  };

  const PhotographerInfo = ({ avatarSize = 'w-8 h-8', textClass = 'text-white text-sm font-medium drop-shadow-lg', ringClass = 'ring-2 ring-white/50', fallbackBg = 'bg-white/20 backdrop-blur-sm', fallbackText = 'text-white' }) => (
    <div
      className={`flex items-center space-x-2 ${photographerClickable ? 'cursor-pointer hover:opacity-80' : ''}`}
      onClick={photographerClickable ? handlePhotographerClick : undefined}
      data-testid="card-photographer-info"
    >
      {photographerImage ? (
        <img
          src={photographerImage}
          alt={`${photographerName} Photographer on Explora Mauritius`}
          className={`${avatarSize} rounded-full object-cover ${ringClass}`}
        />
      ) : (
        <div className={`${avatarSize} rounded-full ${fallbackBg} flex items-center justify-center ${fallbackText} text-xs font-medium ${ringClass}`}>
          {photographerName.charAt(0).toUpperCase()}
        </div>
      )}
      <span className={textClass}>
        {photographerName}
      </span>
    </div>
  );

  const TagBadges = ({ maxTags = 2, className = 'px-2 py-1 bg-white/90 text-gray-800 text-xs rounded-md drop-shadow-lg' }) => {
    if (!image.tags || image.tags.length === 0) return null;
    return (
      <div className="flex items-center space-x-2" data-testid="card-tags">
        {image.tags.slice(0, maxTags).map((tag, idx) => (
          <span
            key={idx}
            className={`${className} ${tagsClickable ? 'cursor-pointer hover:bg-white' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (tagsClickable) handleTagClick(e, tag);
            }}
            data-testid={`card-tag-${idx}`}
          >
            {tag}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="relative group break-inside-avoid mb-6" data-testid="image-card">
      <Link to={photoUrl}>
        <div
          className="relative cursor-zoom-in overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            src={image.image_urls?.thumbnail || image.new_filename}
            alt={image.title}
            className={cropToSquare ? "w-full aspect-square object-cover" : "w-full h-auto object-cover"}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            {...(priority ? { fetchpriority: "high" } : {})}
            {...(cropToSquare
              ? { width: 600, height: 600 }
              : (image.height_500 ? { width: 500, height: image.height_500 } : {}))}
          />
          
          {/* Desktop Hover Overlay */}
          <div
            className={`hidden md:block absolute inset-0 bg-black/40 transition-opacity duration-300 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Upper row: left = photographer, right = like */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
              {showPhotographer && <PhotographerInfo />}
              
              {showLikeFinal && (
                <div
                  className={`flex items-center space-x-1 px-2 py-1 bg-white/90 hover:bg-white rounded-lg transition-colors ${!showPhotographer ? 'ml-auto' : ''}`}
                  onClick={(e) => e.preventDefault()}
                  data-testid="card-like-btn"
                >
                  <LikeButton
                    imageId={image.id}
                    initialLikes={image.likes || 0}
                    size="small"
                    showCount={true}
                  />
                </div>
              )}
            </div>

            {/* Lower row: left = views, right = tags */}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1 text-white" data-testid="card-views">
                  <Eye className="w-4 h-4" />
                  <span className="text-sm drop-shadow-lg">{image.views?.toLocaleString() || 0}</span>
                </div>
                
                {showTags && <TagBadges />}
              </div>
            </div>
          </div>

          {/* Mobile Click Indicator */}
          <div className="md:hidden absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow-lg">
            <ChevronRight className="w-4 h-4 text-gray-700" />
          </div>
        </div>

        {/* Mobile Info Section */}
        <div className="md:hidden bg-white py-2">
          <div className="flex items-center justify-between mb-2">
            {showPhotographer && (
              <PhotographerInfo
                avatarSize="w-7 h-7"
                textClass="text-sm font-medium text-gray-900 truncate"
                ringClass=""
                fallbackBg="bg-gray-200"
                fallbackText="text-gray-700"
              />
            )}

            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-gray-600">
                <Eye className="w-4 h-4" />
                <span className="text-sm">{image.views?.toLocaleString() || 0}</span>
              </div>
              {showLikeFinal && (
                <div onClick={(e) => e.preventDefault()}>
                  <LikeButton
                    imageId={image.id}
                    initialLikes={image.likes || 0}
                    size="small"
                  />
                </div>
              )}
            </div>
          </div>
          {showTags && (
            <div className="mt-1">
              <TagBadges maxTags={3} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded" />
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ImageCard;